import { CartItem } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import React, { PropsWithChildren, useMemo } from 'react';
import ProductImage from '../ProductImage';
import { ProductName, SaleItem, SaleItemInfo, SaleItemLabelValue } from './styles';
import { formatItemSchedule } from '@src/common/utils/formatItemSchedule';
import { groupTariffsByType } from '@src/domains/tariffs';

interface CartSaleItemProps extends PropsWithChildren {
  item: CartItem;
  showPlaceholderImage?: boolean;
}

const CartSaleItem = ({ item, showPlaceholderImage = false, children }: CartSaleItemProps) => {
  const { i18n } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();

  const tariffs = useMemo(() => groupTariffsByType(item.tariffs), [item.tariffs]);

  return (
    <SaleItem key={item.id}>
      <ProductImage product={item.product} showPlaceholderImage={showPlaceholderImage} />

      <SaleItemInfo>
        <ProductName>{item.product.name}</ProductName>

        <SaleItemLabelValue>
          <p>{i18n('direct_link_summary_date_and_time')}</p>
          <p>
            {formatItemSchedule(item, {
              datePrefix: i18n('checkout_purchase_summary_day'),
              fallbackText: i18n('checkout_purchase_summary_without_date'),
            })}
          </p>
        </SaleItemLabelValue>

        {tariffs.map((tariff) => (
          <SaleItemLabelValue key={tariff.tariff.id}>
            <p>
              {tariff.tariff.type.name} - {tariff.quantity}
            </p>
            <p>{formatCurrencyInfo(tariff.costPreview.unitPriceCurrencyInfo)}</p>
          </SaleItemLabelValue>
        ))}

        {item?.resources.map((saleResource) => (
          <SaleItemLabelValue key={saleResource.resource.name}>
            <p>{saleResource.resource.formattedName}</p>
            <p>{saleResource.minPriceCurrencyInfo ? formatCurrencyInfo(saleResource.minPriceCurrencyInfo) : null}</p>
          </SaleItemLabelValue>
        ))}

        {item.additionals.map((saleAdditional) => (
          <SaleItemLabelValue key={saleAdditional.additional.id}>
            <p>
              {saleAdditional.additional.title} - {saleAdditional.quantity}
            </p>

            <p>{formatCurrencyInfo(saleAdditional.amountCurrencyInfo)}</p>
          </SaleItemLabelValue>
        ))}

        {children}
      </SaleItemInfo>
    </SaleItem>
  );
};

export default CartSaleItem;
